import type UserCartStore from '../../UserData/_stores/UserCartStore'
import { getItemTrackingId } from './CartTracking'

type MetaCheckoutEventName = 'InitiateCheckout' | 'AddPaymentInfo' | 'Purchase'
type MetaStandaloneEventName = 'AddToCart' | 'CompleteRegistration' | 'Contact'
type MetaEventName = MetaCheckoutEventName | MetaStandaloneEventName

interface MetaContent {
  id: string
  quantity: number
  item_price: number
}

export function buildMetaCartPayload(cart: UserCartStore) {
  const subtotal = cart.get('subtotal').toDinero()
  const discount = cart.get('discount').toDinero()
  const value = Math.max(0, subtotal.subtract(discount).toUnit())
  const currency = subtotal.getCurrency()
  let numItems = 0
  const contents: MetaContent[] = []
  const content_ids: string[] = []

  cart.get('items').forEach((item) => {
    numItems += item.get('quantity')
    const id = getItemTrackingId(item)

    contents.push({
      id,
      quantity: item.get('quantity'),
      item_price: item.get('price').toDinero().toUnit()
    })
    content_ids.push(id)
  })

  return {
    content_type: 'product',
    content_ids,
    value,
    num_items: numItems,
    currency,
    contents
  }
}

export function getMetaCheckoutEventId(eventName: MetaCheckoutEventName, cart: UserCartStore, scope = ''): string {
  if (eventName === 'Purchase') {
    return `purchase:${cart.get('ref_id')}`
  }

  if (eventName === 'AddPaymentInfo') {
    return `add-payment-info:${cart.get('ref_id')}:${scope}:${Date.now()}:${Math.random().toString(36).slice(2)}`
  }

  const key = `meta-event:${eventName}:${cart.get('ref_id')}:${scope || 'default'}`
  const existing = window.sessionStorage.getItem(key)
  if (existing) {
    return existing
  }

  const eventId = `initiate-checkout:${cart.get('ref_id')}:${Date.now()}:${Math.random().toString(36).slice(2)}`
  window.sessionStorage.setItem(key, eventId)

  return eventId
}

export function getMetaStandaloneEventId(eventName: MetaStandaloneEventName, scope = 'default'): string {
  const slug = eventName.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()

  return `${slug}:${scope}:${Date.now()}:${Math.random().toString(36).slice(2)}`
}

export function appendMetaEventData(fd: FormData, eventId: string, eventSourceUrl = window.location.href): void {
  fd.append('meta_event_id', eventId)
  fd.append('meta_event_source_url', eventSourceUrl)
}

export function trackMetaEvent(eventName: MetaEventName, payload: Record<string, unknown>, eventId: string): void {
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  window.fbq && window.fbq('track', eventName, payload, { eventID: eventId })
}

export function trackMetaBrowserEvent(eventName: MetaCheckoutEventName, cart: UserCartStore, eventId: string): void {
  const payload = {
    ...buildMetaCartPayload(cart),
    ...(eventName === 'Purchase' ? { order_id: cart.get('ref_id') } : {})
  }

  trackMetaEvent(eventName, payload, eventId)
}

export function queueMetaServerEvent(
  eventName: Exclude<MetaCheckoutEventName, 'Purchase'>,
  cart: UserCartStore,
  eventId: string,
  paymentMethod?: string
): void {
  const fd = new FormData()
  fd.append('event_name', eventName)
  fd.append('event_id', eventId)
  fd.append('id_cart', String(cart.get('id')))
  fd.append('event_source_url', window.location.href)

  if (paymentMethod) {
    fd.append('payment_method', paymentMethod)
  }

  if (navigator.sendBeacon?.('ajax/api/cart/meta-conversion-event', fd)) {
    return
  }

  void fetch('ajax/api/cart/meta-conversion-event', {
    method: 'POST',
    body: fd,
    keepalive: true
  }).catch(() => undefined)
}
